import { NavLink, Outlet } from "react-router";

import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";

/**
 * The console shell: a fixed sidebar and the routed page beside it.
 *
 * Rendered only for `status === "authorised"` — see `app.tsx`. Nothing here
 * checks the role, because by the time this mounts the question is settled.
 */

type Item = {
  to: string;
  label: string;
  /** Set until the endpoints behind the screen exist. */
  pending?: boolean;
};

const NAV: Item[] = [
  { to: "/orders", label: "Orders" },
  { to: "/payments", label: "Payments" },
  { to: "/shipments", label: "Shipments" },
  { to: "/products", label: "Products" },
  { to: "/stock", label: "Stock" },
  { to: "/messages", label: "Communications" },
];

export function Layout() {
  const { user, signOut } = useAuth();

  return (
    <div className="flex min-h-full">
      <aside className="flex w-[220px] shrink-0 flex-col border-r border-line bg-surface">
        <div className="border-b border-line px-5 py-4">
          <p className="text-[11px] font-bold tracking-[0.14em] text-faint uppercase">Arrow</p>
          <p className="text-[14px] font-semibold text-ink">Admin</p>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center justify-between rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors",
                  isActive ? "bg-ink text-white" : "text-body hover:bg-canvas hover:text-ink",
                )
              }
            >
              {item.label}
              {/* An honest label beats a screen that loads nothing and looks
                  broken. Removed as each phase lands. */}
              {item.pending ? (
                <span className="rounded-full bg-warn-tint px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-warn uppercase">
                  Soon
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <p className="truncate px-3 pb-2 text-[12px] text-muted" title={user?.email ?? ""}>
            {user?.name ?? user?.email ?? "Signed in"}
          </p>
          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full rounded-lg border border-line px-3 py-2 text-[12.5px] font-semibold text-body transition-colors hover:border-line-strong hover:text-ink"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
